import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from '@daypicker/react';
import type { ChronologyInfo } from '@/hooks/useChronologies';
import type { ChronologyEventLight } from '@/hooks/useChronology';
import {
  getEventCategoryName,
  isFallbackCategoryName,
} from './styles';

function eventMatchesSearch(ev: ChronologyEventLight, query: string): boolean {
  if (!query) return true;
  if (ev.description.toLowerCase().includes(query)) return true;
  if (ev.detail?.toLowerCase().includes(query)) return true;
  if (ev.created_by_label?.toLowerCase().includes(query)) return true;
  if (ev.tags?.some(tag =>
    tag.label.toLowerCase().includes(query)
    || tag.tag_key.toLowerCase().includes(query)
    || (tag.group_label || '').toLowerCase().includes(query)
  )) return true;
  if (ev.source_document_names.some(name => name.toLowerCase().includes(query))) return true;
  return ev.entity_names.some(name => name.toLowerCase().includes(query));
}

function matchesSelectedFacet(values: readonly string[], selected: ReadonlySet<string>): boolean {
  return selected.size === 0 || values.some(value => selected.has(value));
}

function chronologyYear(date: Date | null | undefined): number | null {
  return date ? date.getUTCFullYear() : null;
}

export interface TypeEntry {
  key: string;
  label: string;
  category: string;
}

export interface ChronologyOption {
  type: string;
  name: string;
  count: number;
}

export const UNCATEGORIZED = '__uncategorized__';

function eventTagEntries(ev: ChronologyEventLight): TypeEntry[] {
  return (ev.tags || []).map(tag => ({
    key: tag.tag_key,
    label: tag.label || tag.tag_key,
    category: getEventCategoryName(tag.group_label || tag.group_key || null),
  }));
}

function eventCategoryNames(ev: ChronologyEventLight): string[] {
  const entries = eventTagEntries(ev);
  if (entries.length) {
    return Array.from(new Set(entries.map(entry => entry.category)));
  }
  return [];
}

export type ChronologyFilters = ReturnType<typeof useChronologyFilters>;

interface UseChronologyFiltersArgs {
  events: ChronologyEventLight[];
  chronologies: ChronologyInfo[];
  searchQueryProp?: string;
  onSearchChange?: (value: string) => void;
}

export function useChronologyFilters({
  events,
  chronologies,
  searchQueryProp,
  onSearchChange,
}: UseChronologyFiltersArgs) {
  const [searchQueryLocal, setSearchQueryLocal] = useState('');
  const searchQuery = searchQueryProp ?? searchQueryLocal;
  const setSearchQuery = onSearchChange ?? setSearchQueryLocal;
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());
  const [excludedTypes, setExcludedTypes] = useState<Set<string>>(new Set());
  const [activeChronologies, setActiveChronologies] = useState<Set<string>>(new Set());
  const [activeEntities, setActiveEntities] = useState<Set<string>>(new Set());
  const [activeCreators, setActiveCreators] = useState<Set<string>>(new Set());
  const [activeSourceEntities, setActiveSourceEntities] = useState<Set<string>>(new Set());
  const [activeSources, setActiveSources] = useState<Set<string>>(new Set());
  const [showUndated, setShowUndated] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const dateFrom = dateRange?.from ? dateRange.from.toISOString().split('T')[0] : '';
  const dateTo = dateRange?.to ? dateRange.to.toISOString().split('T')[0] : '';

  const chronoIdsKey = chronologies.map(c => c.id).sort().join(',');
  useEffect(() => {
    void chronoIdsKey;
    setActiveCategories(new Set());
    setExcludedTypes(new Set());
    setActiveChronologies(new Set());
  }, [chronoIdsKey]);

  const normalizedSearchQuery = searchQuery.toLowerCase().trim();

  const eventPassesFilters = useCallback((
    ev: ChronologyEventLight,
    options: {
      ignoreCategories?: boolean;
      ignoreTypes?: boolean;
      ignoreEntities?: boolean;
      ignoreCreators?: boolean;
      ignoreSourceEntities?: boolean;
      ignoreSources?: boolean;
      ignoreChronologies?: boolean;
      ignoreDate?: boolean;
      ignoreSearch?: boolean;
    } = {},
  ) => {
    const tagEntries = eventTagEntries(ev);
    if (!options.ignoreCategories && activeCategories.size > 0 && !eventCategoryNames(ev).some(name => activeCategories.has(name))) return false;
    if (!options.ignoreTypes) {
      if (tagEntries.length > 0) {
        const relevantTags = (!options.ignoreCategories && activeCategories.size > 0)
          ? tagEntries.filter(entry => activeCategories.has(entry.category))
          : tagEntries;
        if (relevantTags.length > 0 && !relevantTags.some(entry => !excludedTypes.has(entry.key))) return false;
      }
    }
    if (!options.ignoreEntities && !matchesSelectedFacet(ev.entity_names, activeEntities)) return false;
    if (!options.ignoreCreators && activeCreators.size > 0 && !(ev.created_by_label && activeCreators.has(ev.created_by_label))) return false;
    if (!options.ignoreSourceEntities && !matchesSelectedFacet(ev.source_party_names, activeSourceEntities)) return false;
    if (!options.ignoreSources && !matchesSelectedFacet(ev.source_document_names, activeSources)) return false;
    if (!options.ignoreChronologies && activeChronologies.size > 0 && !(ev.chronology_type && activeChronologies.has(ev.chronology_type))) return false;
    if (!options.ignoreDate && dateFrom && ev.temporal_lower && ev.temporal_lower.toISOString() < dateFrom) return false;
    if (!options.ignoreDate && dateTo && ev.temporal_lower && ev.temporal_lower.toISOString() > dateTo) return false;
    if (!options.ignoreSearch && !eventMatchesSearch(ev, normalizedSearchQuery)) return false;
    return true;
  }, [
    activeCategories,
    excludedTypes,
    activeEntities,
    activeCreators,
    activeSourceEntities,
    activeSources,
    activeChronologies,
    dateFrom,
    dateTo,
    normalizedSearchQuery,
  ]);

  const entityEventCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of events) {
      if (!eventPassesFilters(ev, { ignoreEntities: true })) continue;
      for (const name of ev.entity_names) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    for (const name of activeEntities) {
      if (!counts.has(name)) counts.set(name, 0);
    }
    return counts;
  }, [events, eventPassesFilters, activeEntities]);

  const sourceEntityEventCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of events) {
      if (!eventPassesFilters(ev, { ignoreSourceEntities: true })) continue;
      for (const name of ev.source_party_names) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    for (const name of activeSourceEntities) {
      if (!counts.has(name)) counts.set(name, 0);
    }
    return counts;
  }, [events, eventPassesFilters, activeSourceEntities]);

  const creatorEventCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of events) {
      if (!eventPassesFilters(ev, { ignoreCreators: true })) continue;
      if (!ev.created_by_label) continue;
      counts.set(ev.created_by_label, (counts.get(ev.created_by_label) ?? 0) + 1);
    }
    for (const name of activeCreators) {
      if (!counts.has(name)) counts.set(name, 0);
    }
    return counts;
  }, [events, eventPassesFilters, activeCreators]);

  const hasMultipleCreators = useMemo(
    () => new Set(events.map(ev => ev.created_by_label).filter(Boolean)).size > 1,
    [events],
  );

  const sourceEventCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of events) {
      if (!eventPassesFilters(ev, { ignoreSources: true })) continue;
      for (const name of ev.source_document_names) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    for (const name of activeSources) {
      if (!counts.has(name)) counts.set(name, 0);
    }
    return counts;
  }, [events, eventPassesFilters, activeSources]);

  const eventYears = useMemo(() => {
    const years = new Set<number>();
    for (const ev of events) {
      const year = chronologyYear(ev.temporal_lower);
      if (year !== null) years.add(year);
    }
    return Array.from(years).sort();
  }, [events]);

  const categoryGroups = useMemo(() => {
    const set = new Set<string>();
    const configured = chronologies.filter(c =>
      activeChronologies.size === 0 || activeChronologies.has(c.chronology_type),
    );
    for (const chronology of configured) {
      for (const group of chronology.tag_groups || []) set.add(group.label);
      for (const tag of chronology.tags || []) {
        if (tag.group_label) set.add(tag.group_label);
      }
    }
    for (const ev of events) {
      if (activeChronologies.size > 0 && !(ev.chronology_type && activeChronologies.has(ev.chronology_type))) continue;
      for (const category of eventCategoryNames(ev)) set.add(category);
    }
    return Array.from(set);
  }, [events, activeChronologies, chronologies]);

  const visibleCategoryGroups = useMemo(() => {
    if (categoryGroups.length === 1 && isFallbackCategoryName(categoryGroups[0])) return [];
    return categoryGroups;
  }, [categoryGroups]);

  const typesByCategory = useMemo(() => {
    const map = new Map<string, TypeEntry[]>();
    const configured = chronologies.filter(c =>
      activeChronologies.size === 0 || activeChronologies.has(c.chronology_type),
    );
    const seen = new Set<string>();
    for (const chronology of configured) {
      for (const tag of chronology.tags || []) {
        if (seen.has(tag.tag_key)) continue;
        seen.add(tag.tag_key);
        const category = getEventCategoryName(tag.group_label || tag.group_key || null);
        const bucket = category || UNCATEGORIZED;
        let items = map.get(bucket);
        if (!items) {
          items = [];
          map.set(bucket, items);
        }
        items.push({
          key: tag.tag_key,
          label: tag.label,
          category,
        });
      }
    }
    for (const ev of events) {
      if (activeChronologies.size > 0 && !(ev.chronology_type && activeChronologies.has(ev.chronology_type))) continue;
      for (const entry of eventTagEntries(ev)) {
        if (!entry.key || seen.has(entry.key)) continue;
        seen.add(entry.key);
        const bucket = entry.category || UNCATEGORIZED;
        let items = map.get(bucket);
        if (!items) {
          items = [];
          map.set(bucket, items);
        }
        items.push(entry);
      }
    }
    return map;
  }, [events, activeChronologies, chronologies]);

  const chronologyOptions = useMemo<ChronologyOption[]>(() => {
    const counts = new Map<string, number>();
    for (const ev of events) {
      if (!eventPassesFilters(ev, { ignoreChronologies: true })) continue;
      if (!ev.chronology_type) continue;
      counts.set(ev.chronology_type, (counts.get(ev.chronology_type) ?? 0) + 1);
    }
    if (chronologies.length > 0) {
      return chronologies.map(chronology => ({
        type: chronology.chronology_type,
        name: chronology.name,
        count: counts.get(chronology.chronology_type) ?? 0,
      }));
    }

    const map = new Map<string, { name: string; count: number }>();
    for (const ev of events) {
      if (!eventPassesFilters(ev, { ignoreChronologies: true })) continue;
      if (!ev.chronology_type || !ev.chronology_name) continue;
      const current = map.get(ev.chronology_type);
      if (current) current.count += 1;
      else map.set(ev.chronology_type, { name: ev.chronology_name, count: 1 });
    }
    return Array.from(map.entries()).map(([type, value]) => ({ type, name: value.name, count: value.count }));
  }, [events, chronologies, eventPassesFilters]);

  const { categoryCounts, typeCounts } = useMemo(() => {
    const categories = new Map<string, number>();
    const types = new Map<string, number>();
    for (const ev of events) {
      if (eventPassesFilters(ev, { ignoreCategories: true })) {
        for (const category of eventCategoryNames(ev)) {
          categories.set(category, (categories.get(category) ?? 0) + 1);
        }
      }
      // Keep type-popover structure stable when toggling a category pill:
      // type visibility/counts should respond to the other filters, but not
      // collapse just because the category facet itself is active.
      if (eventPassesFilters(ev, { ignoreTypes: true, ignoreCategories: true })) {
        for (const entry of eventTagEntries(ev)) {
          types.set(entry.key, (types.get(entry.key) ?? 0) + 1);
        }
      }
    }
    return { categoryCounts: categories, typeCounts: types };
  }, [events, eventPassesFilters]);

  const visibleTypesByCategory = useMemo(() => {
    const filtered = new Map<string, TypeEntry[]>();
    for (const [bucket, items] of typesByCategory.entries()) {
      const visibleItems = items.filter(item =>
        (typeCounts.get(item.key) ?? 0) > 0 || excludedTypes.has(item.key),
      );
      if (visibleItems.length > 0) {
        filtered.set(bucket, visibleItems);
      }
    }

    return filtered;
  }, [typesByCategory, typeCounts, excludedTypes]);

  const allVisibleTypeKeys = useMemo(() => {
    const set = new Set<string>();
    for (const items of visibleTypesByCategory.values()) {
      for (const item of items) set.add(item.key);
    }
    return set;
  }, [visibleTypesByCategory]);

  const tagsByCategoryName = useMemo(() => {
    const map = new Map<string, TypeEntry[]>();
    for (const items of visibleTypesByCategory.values()) {
      for (const item of items) {
        const current = map.get(item.category);
        if (current) current.push(item);
        else map.set(item.category, [item]);
      }
    }
    return map;
  }, [visibleTypesByCategory]);

  const tagCategoryNames = useMemo(() => {
    const set = new Set(visibleCategoryGroups);
    for (const name of tagsByCategoryName.keys()) set.add(name);
    return Array.from(set);
  }, [visibleCategoryGroups, tagsByCategoryName]);

  const allVisibleCategoryNames = useMemo(() => new Set(tagCategoryNames), [tagCategoryNames]);

  useEffect(() => {
    setActiveCategories(prev => {
      if (prev.size === 0) return prev;
      const valid = new Set(allVisibleCategoryNames);
      const next = new Set<string>();
      for (const key of prev) if (valid.has(key)) next.add(key);
      return next.size === prev.size ? prev : next;
    });
  }, [allVisibleCategoryNames]);

  useEffect(() => {
    setExcludedTypes(prev => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      for (const key of prev) if (allVisibleTypeKeys.has(key)) next.add(key);
      return next.size === prev.size ? prev : next;
    });
  }, [allVisibleTypeKeys]);

  const filteredEvents = useMemo(() => events.filter(ev => eventPassesFilters(ev)), [events, eventPassesFilters]);
  const { datedEvents, undatedEvents } = useMemo(() => {
    const dated: ChronologyEventLight[] = [];
    const undated: ChronologyEventLight[] = [];
    for (const ev of filteredEvents) {
      if (ev.temporal_lower) dated.push(ev);
      else undated.push(ev);
    }
    return { datedEvents: dated, undatedEvents: undated };
  }, [filteredEvents]);

  const hasActiveFilters = activeCategories.size > 0
    || excludedTypes.size > 0
    || activeEntities.size > 0
    || activeCreators.size > 0
    || activeSourceEntities.size > 0
    || activeSources.size > 0
    || activeChronologies.size > 0
    || searchQuery.trim().length > 0
    || Boolean(dateFrom)
    || Boolean(dateTo);

  // Tag selection reads as a checklist: nothing ticked shows everything,
  // ticking narrows. It is stored as included categories plus excluded tags,
  // and excluded tags only ever belong to an included category.
  // Writing a selection uses every tag in the category, including ones the
  // popover has hidden, so a hidden sibling cannot stay selected. The checklist
  // state follows only the tags on screen, so clearing the last visible tag
  // clears the category.
  const categoryTagKeys = useCallback((category: string) => {
    const keys: string[] = [];
    for (const items of typesByCategory.values()) {
      for (const item of items) {
        if (item.category === category) keys.push(item.key);
      }
    }
    return keys;
  }, [typesByCategory]);

  const visibleCategoryTagKeys = useCallback(
    (category: string) => (tagsByCategoryName.get(category) ?? []).map(item => item.key),
    [tagsByCategoryName],
  );

  const tagSelectionState = useCallback((category: string): 'all' | 'some' | 'none' => {
    if (!activeCategories.has(category)) return 'none';
    const visible = visibleCategoryTagKeys(category);
    if (visible.length === 0) return categoryTagKeys(category).length === 0 ? 'all' : 'none';
    return visible.some(key => excludedTypes.has(key)) ? 'some' : 'all';
  }, [activeCategories, excludedTypes, categoryTagKeys, visibleCategoryTagKeys]);

  const isTagSelected = useCallback(
    (typeKey: string, category: string) => activeCategories.has(category) && !excludedTypes.has(typeKey),
    [activeCategories, excludedTypes],
  );

  const deselectCategory = useCallback((category: string) => {
    const keys = categoryTagKeys(category);
    setActiveCategories(prev => {
      const next = new Set(prev);
      next.delete(category);
      return next;
    });
    setExcludedTypes(prev => {
      const next = new Set(prev);
      for (const key of keys) next.delete(key);
      return next;
    });
  }, [categoryTagKeys]);

  const toggleCategorySelection = useCallback((category: string) => {
    if (tagSelectionState(category) === 'all') {
      deselectCategory(category);
      return;
    }
    const keys = categoryTagKeys(category);
    setActiveCategories(prev => new Set(prev).add(category));
    setExcludedTypes(prev => {
      const next = new Set(prev);
      for (const key of keys) next.delete(key);
      return next;
    });
  }, [tagSelectionState, deselectCategory, categoryTagKeys]);

  const toggleTagSelection = useCallback((typeKey: string, category: string) => {
    const keys = categoryTagKeys(category);
    if (!activeCategories.has(category)) {
      setActiveCategories(prev => new Set(prev).add(category));
      setExcludedTypes(prev => {
        const next = new Set(prev);
        for (const key of keys) if (key !== typeKey) next.add(key);
        next.delete(typeKey);
        return next;
      });
      return;
    }
    if (excludedTypes.has(typeKey)) {
      setExcludedTypes(prev => {
        const next = new Set(prev);
        next.delete(typeKey);
        return next;
      });
      return;
    }
    const othersSelected = visibleCategoryTagKeys(category).some(key => key !== typeKey && !excludedTypes.has(key));
    if (!othersSelected) {
      deselectCategory(category);
      return;
    }
    setExcludedTypes(prev => new Set(prev).add(typeKey));
  }, [activeCategories, excludedTypes, categoryTagKeys, visibleCategoryTagKeys, deselectCategory]);

  const showOnlyType = useCallback((typeKey: string, category: string) => {
    setActiveCategories(new Set([category]));
    setExcludedTypes(new Set(categoryTagKeys(category).filter(key => key !== typeKey)));
  }, [categoryTagKeys]);

  const selectedTagCount = useMemo(() => {
    let count = 0;
    for (const category of activeCategories) {
      const visible = visibleCategoryTagKeys(category);
      if (visible.length === 0) {
        if (categoryTagKeys(category).length === 0) count += 1;
        continue;
      }
      count += visible.filter(key => !excludedTypes.has(key)).length;
    }
    return count;
  }, [activeCategories, excludedTypes, categoryTagKeys, visibleCategoryTagKeys]);

  const toggleEntity = useCallback((name: string) => {
    setActiveEntities(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const toggleCreator = useCallback((name: string) => {
    setActiveCreators(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const toggleSourceEntity = useCallback((name: string) => {
    setActiveSourceEntities(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const toggleSource = useCallback((name: string) => {
    setActiveSources(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const toggleChronology = useCallback((type: string) => {
    setActiveChronologies(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const clearTagSelection = useCallback(() => {
    setActiveCategories(new Set());
    setExcludedTypes(new Set());
  }, []);

  const clearChronologies = useCallback(() => {
    setActiveChronologies(new Set());
  }, []);

  const clearEntities = useCallback(() => {
    setActiveEntities(new Set());
  }, []);

  const clearCreators = useCallback(() => {
    setActiveCreators(new Set());
  }, []);

  const clearSourceEntities = useCallback(() => {
    setActiveSourceEntities(new Set());
  }, []);

  const clearSources = useCallback(() => {
    setActiveSources(new Set());
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setActiveCategories(new Set());
    setExcludedTypes(new Set());
    setActiveEntities(new Set());
    setActiveCreators(new Set());
    setActiveSourceEntities(new Set());
    setActiveSources(new Set());
    setActiveChronologies(new Set());
    setShowUndated(false);
    setDateRange(undefined);
  }, [setSearchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    activeCategories,
    excludedTypes,
    activeChronologies,
    activeEntities,
    activeCreators,
    activeSourceEntities,
    activeSources,
    showUndated,
    dateRange,
    hasMultipleCreators,
    dateFrom,
    dateTo,
    entityEventCounts,
    creatorEventCounts,
    sourceEntityEventCounts,
    sourceEventCounts,
    eventYears,
    tagCategoryNames,
    tagsByCategoryName,
    chronologyOptions,
    categoryCounts,
    typeCounts,
    selectedTagCount,
    filteredEvents,
    datedEvents,
    undatedEvents,
    hasActiveFilters,
    setDateRange,
    setShowUndated,
    tagSelectionState,
    isTagSelected,
    toggleCategorySelection,
    toggleTagSelection,
    deselectCategory,
    clearTagSelection,
    toggleEntity,
    toggleCreator,
    toggleSourceEntity,
    toggleSource,
    toggleChronology,
    clearChronologies,
    clearEntities,
    clearCreators,
    clearSourceEntities,
    clearSources,
    clearFilters,
    showOnlyType,
  };
}
